/**
 * WorkforceServiceCatalogue — the appointment catalogue, ONE document behind three
 * audiences (REQ-128, 2026-08-03).
 *
 * Riley (Operations admin) CONFIGURES: which lines of business a branch runs, which
 * services a branch switches off, and which line of business each advisor is tied to.
 * The branch manager and the advisor READ — same rows, same numbers, no controls
 * (operator ruling: "make those services available and viewable only, no configuration
 * change but provide a view into the service").
 *
 * The content is the operator's FinServ family tree: 7 lines of business → 24 intents →
 * 79 services. Three consequences for this screen:
 *  · Grouping is not decoration. 79 rows in a flat table is unusable, so the surface is
 *    LOB → intent → service with search across all three, and the intent level is
 *    LABELLED from the server (`intentLabel`), not hardcoded.
 *  · Pre/post time is real scheduling time. Each row shows duration and the TOTAL BLOCK
 *    it consumes (pre + duration + post), because that is what actually comes out of an
 *    advisor's day.
 *  · Booking windows are literal per row: where the source had no value the row says
 *    "not set" rather than inheriting a guess.
 *
 * Ops: getServiceCatalogue · setBranchLob · setBranchService · service/LOB/intent CRUD
 * (all awaited; the read-only audiences never see the write affordances).
 */
import { useEffect, useMemo, useState } from "react";
import { Btn, DatePicker, Icon, Pill, T, alpha } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { Drawer, Modal } from "../../shell/Overlay";
import { toast } from "../../shell/toast";
import { useBusy } from "./useWorkforce";
import {
  getServiceCatalogue, setBranchLob, setBranchService, getMyServiceScope,
  createCatalogueService, updateCatalogueService, removeCatalogueService,
  createCatalogueLob, createCatalogueIntent, importCatalogueServices,
  type ServiceCatalogue, type CatalogueService, type ServiceDraft, type CatalogueQuestionGroup,
} from "./wf-api";
import { IMPORT_COLUMNS, importTemplateCsv, parseImportCsv, validateImportRows, downloadText, failureReportCsv, issueRowsCsv, type ParsedImport, type ImportValidation } from "./catalogue-import";
import { getUser } from "../../shell/access";
import { useT } from "../../lib/i18n";

const mono = { fontFamily: T.fontMono };
const card = { border: `1px solid ${T.border}`, borderRadius: 12, background: T.white, overflow: "hidden" as const, flexShrink: 0 };
const cap = { fontSize: 11, color: T.soft, textTransform: "uppercase" as const, letterSpacing: 0.05, fontWeight: 700 };
/* Import-history columns (operator, 2026-08-04): Status · Import name · Upload date/time ·
   Completion date/time · download. One constant shared by the header and the rows, so the two
   cannot drift apart; every flexible track carries a floor so the table never overflows its card. */
const IMPORT_COLS = "150px minmax(170px,1.4fr) minmax(160px,1fr) minmax(140px,1fr) 112px";

function channelLabel(c: CatalogueService["channels"], t: (k: string, f: string) => string): string {
  const parts: string[] = [];
  if (c.inPerson) parts.push(t("workforce.catalogue.branch", "Branch"));
  if (c.phone) parts.push(t("workforce.catalogue.phone", "Phone"));
  if (c.video) parts.push(t("workforce.catalogue.virtual", "Virtual"));
  return parts.join(" · ") || t("workforce.catalogue.noChannel", "No channel set");
}

/** Duration + the pre/post shoulders, drawn to scale — the same visual language the
 *  day view uses: solid core, lighter shoulders. */
function BlockBar({ s }: { s: CatalogueService }) {
  const total = s.blockMin || (s.durationMin ?? 0);
  if (!total) return null;
  const pct = (n: number) => `${Math.max(0, (n / total) * 100)}%`;
  return (
    <span title={`${s.preMin} + ${s.durationMin} + ${s.postMin}`} style={{ display: "flex", width: 96, height: 8, borderRadius: 4, overflow: "hidden", background: T.n6 }}>
      {s.preMin ? <span style={{ width: pct(s.preMin), background: alpha(T.p1, 28) }} /> : null}
      <span style={{ width: pct(s.durationMin ?? 0), background: T.p1 }} />
      {s.postMin ? <span style={{ width: pct(s.postMin), background: alpha(T.p1, 28) }} /> : null}
    </span>
  );
}

export function ServiceDetail({ svc, intentName, lobName, groupName, onClose }: { svc: CatalogueService; intentName: string; lobName: string; groupName: (id: string | null) => string | null; onClose: () => void }) {
  const t = useT();
  const row = (label: string, value: React.ReactNode) => (
    <div style={{ display: "grid", gridTemplateColumns: "150px 1fr", gap: 12, padding: "11px 0", borderBottom: `1px solid ${T.n6}` }}>
      <span style={{ fontSize: 12, color: T.soft, fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: 13, color: T.n1 }}>{value}</span>
    </div>
  );
  const notSet = <span style={{ color: T.soft }}>{t("workforce.catalogue.notSet", "not set")}</span>;
  const mods = svc.modules ?? { appointments: false, queueing: false, events: false };
  const modList = [
    mods.appointments ? t("workforce.catalogue.modAppointments", "Appointments") : null,
    mods.queueing ? t("workforce.catalogue.modQueueing", "Queueing") : null,
    mods.events ? t("workforce.catalogue.modEvents", "Events") : null,
  ].filter(Boolean).join(" · ");
  const qg = svc.questionGroups ?? { appointments: null, queueing: null, events: null };
  const qgRows = ([
    [mods.appointments, t("workforce.catalogue.modAppointments", "Appointments"), qg.appointments],
    [mods.queueing, t("workforce.catalogue.modQueueing", "Queueing"), qg.queueing],
    [mods.events, t("workforce.catalogue.modEvents", "Events"), qg.events],
  ] as const).filter(([on]) => on);
  return (
    <Drawer panel="service" width={520} testid="wfcat-service-drawer" onClose={onClose} title={svc.name} sub={`${lobName} · ${intentName}`}>
      <div style={{ padding: "6px 22px 26px" }}>
        {/* The artwork a customer sees on the journey, at the top — it is the first thing they see
            too, so reviewing the service should start with it. */}
        {svc.imageUrl ? (
          <img src={svc.imageUrl} alt={svc.imageAlt || ""} data-testid="wfcat-detail-image"
            style={{ width: "100%", aspectRatio: "16 / 9", objectFit: "cover", borderRadius: 10, display: "block", margin: "10px 0 4px", background: T.n6 }} />
        ) : null}
        {/* Unavailability is stated first: it changes what everything below means. */}
        {svc.bookableNow === false ? (
          <div style={{ display: "flex", gap: 9, alignItems: "flex-start", padding: "11px 13px", margin: "8px 0 4px", borderRadius: 8, background: alpha(T.orange1, 8), border: `1px solid ${alpha(T.orange1, 30)}` }}>
            <Icon.info size={13} />
            <span style={{ fontSize: 12, color: T.n2, lineHeight: 1.5 }}>
              {svc.unavailableReason === "before-window"
                ? `${t("workforce.catalogue.notBookableUntil", "Not bookable until")} ${svc.disableBefore}. ${t("workforce.catalogue.hiddenFromCustomers", "Customers can't see it yet.")}`
                : svc.unavailableReason === "after-window"
                ? `${t("workforce.catalogue.noLongerBookableSince", "No longer bookable — ended")} ${svc.disableAfter}.`
                : t("workforce.catalogue.archivedShort", "Archived — not bookable.")}
            </span>
          </div>
        ) : null}
        {svc.description ? (
          <p style={{ fontSize: 13, color: T.n2, lineHeight: 1.6, margin: "10px 0 14px" }}>{svc.description}</p>
        ) : null}
        {row(t("workforce.catalogue.lineOfBusiness", "Line of business"), lobName)}
        {row(t("workforce.catalogue.intent", "Intent"), intentName)}
        {row(t("workforce.catalogue.appointmentLength", "Appointment length"), <span style={mono}>{svc.durationMin} {t("workforce.catalogue.min", "min")}</span>)}
        {row(t("workforce.catalogue.totalBlock", "Total block"), (
          <span style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={mono}>{svc.blockMin} {t("workforce.catalogue.min", "min")}</span>
            <BlockBar s={svc} />
            <span style={{ fontSize: 11.5, color: T.soft, ...mono }}>{svc.preMin} + {svc.durationMin} + {svc.postMin}</span>
          </span>
        ))}
        {row(t("workforce.catalogue.bookableVia", "Bookable via"), modList || notSet)}
        {row(t("workforce.catalogue.channels", "Channels"), channelLabel(svc.channels, t))}
        {qgRows.length ? row(t("workforce.catalogue.bookingQuestions", "Booking questions"), (
          <span style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {qgRows.map(([, label, id]) => (
              <span key={label} style={{ fontSize: 12.5 }}>
                <span style={{ color: T.soft }}>{label}: </span>
                {groupName(id) ?? <span style={{ color: T.soft }}>{t("workforce.catalogue.noQuestions", "No questions")}</span>}
              </span>
            ))}
          </span>
        )) : null}
        {row(t("workforce.catalogue.bookedBy", "Who can book it"), svc.bookedBy === "advisor"
          ? t("workforce.catalogue.advisorOnly", "Advisors only")
          : t("workforce.catalogue.customerOrAdvisor", "Customers and advisors"))}
        {svc.disableBefore || svc.disableAfter ? row(t("workforce.catalogue.availableBetween", "Available between"), (
          <span style={mono}>{svc.disableBefore ?? "—"} → {svc.disableAfter ?? "—"}</span>
        )) : null}
        {row(t("workforce.catalogue.minAdvance", "Earliest booking"), svc.minAdvance ?? notSet)}
        {row(t("workforce.catalogue.maxAdvance", "Latest booking"), svc.maxAdvance ?? notSet)}
        {row(t("workforce.catalogue.minCancel", "Cancellation window"), svc.minCancel ?? notSet)}
        <p style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.55, marginTop: 16 }}>
          {t("workforce.catalogue.blockExplainer", "The total block is what comes out of an advisor's day: preparation time, the appointment itself, then wrap-up. Availability reserves the whole block, not just the appointment.")}
        </p>
      </div>
    </Drawer>
  );
}

/* ── Riley's service editor (2026-08-03) ──────────────────────────────────
 * Every catalogue field, in the order a person thinks about a service: what it is, how long
 * it takes (and therefore what it BLOCKS), how it can be delivered, and when it can be booked.
 * Seeded rows are edited through an overlay — the operator's document stays authoritative and
 * the drawer says so, so nobody believes they are rewriting the source file.
 */
const FIELD_LABEL = { fontSize: 11.5, fontWeight: 700, color: T.n2 } as const;
const INPUT = { height: 34, padding: "0 10px", borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 13, fontFamily: T.font, color: T.n1, width: "100%", boxSizing: "border-box" as const };

function TextField({ label, value, onChange, placeholder, hint, testid }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; hint?: string; testid: string;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <span style={FIELD_LABEL}>{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} style={INPUT} />
      {hint ? <span style={{ fontSize: 11, color: T.soft }}>{hint}</span> : null}
    </label>
  );
}
function NumField({ label, value, onChange, suffix, testid }: {
  label: string; value: number; onChange: (n: number) => void; suffix?: string; testid: string;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <span style={FIELD_LABEL}>{label}</span>
      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <input type="number" min={0} step={5} value={String(value)} data-testid={testid}
          onChange={(e) => onChange(Math.max(0, parseInt(e.target.value, 10) || 0))} style={{ ...INPUT, width: 88 }} />
        {suffix ? <span style={{ fontSize: 12, color: T.soft }}>{suffix}</span> : null}
      </span>
    </label>
  );
}

/* Branch scope — a searchable dropdown (operator, 2026-08-03: "should be a drop down with search
 * functionality to find branches, not a pill list"). A bank has hundreds of branches; a chip row
 * only works for three. Whole-network is the first option, not a separate control. */
function BranchPicker({ branches, value, onChange }: { branches: string[]; value: string | null; onChange: (b: string | null) => void }) {
  const t = useT();
  const [open, setOpen] = useState(false); // ui-local
  const [q, setQ] = useState(""); // ui-local
  const all = t("workforce.catalogue.wholeNetwork", "Whole network");
  const hits = branches.filter((b) => b.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, position: "relative" }}>
      <span style={{ fontSize: 11.5, color: T.soft, fontWeight: 600 }}>{t("workforce.catalogue.viewAs", "Showing")}</span>
      <button type="button" data-testid="wfcat-scope" aria-haspopup="listbox" aria-expanded={open} onClick={() => { setOpen((o) => !o); setQ(""); }}
        style={{ display: "inline-flex", alignItems: "center", gap: 8, height: 32, padding: "0 11px", borderRadius: 8, border: `1px solid ${open ? T.p1 : T.border}`, background: T.white, color: T.n1, fontSize: 12.5, fontWeight: 600, fontFamily: T.font, cursor: "pointer", maxWidth: 240 }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value ?? all}</span>
        <Icon.chev size={12} />
      </button>
      {open ? (
        <>
          <span onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <span style={{ position: "absolute", top: 38, right: 0, zIndex: 41, width: 268, padding: 8, borderRadius: 10, border: `1px solid ${T.border}`, background: T.white, boxShadow: T.shadowLg, display: "flex", flexDirection: "column", gap: 6 }}>
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="wfcat-scope-search"
              placeholder={t("workforce.catalogue.searchBranches", "Search branches…")}
              style={{ height: 32, padding: "0 10px", borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 12.5, fontFamily: T.font, color: T.n1 }} />
            <span role="listbox" style={{ display: "flex", flexDirection: "column", maxHeight: 260, overflow: "auto" }}>
              <button type="button" role="option" aria-selected={!value} data-testid="wfcat-scope-option" data-id="__all"
                onClick={() => { onChange(null); setOpen(false); }}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 10px", borderRadius: 6, border: "none", background: !value ? T.p3 : "transparent", color: !value ? T.p1 : T.n1, fontSize: 12.5, fontWeight: !value ? 700 : 500, fontFamily: T.font, textAlign: "left", cursor: "pointer" }}>
                {all}{!value ? <Icon.check size={12} /> : null}
              </button>
              {hits.length === 0 ? (
                <span style={{ padding: "10px", fontSize: 12, color: T.soft }}>{t("workforce.catalogue.noBranchMatch", "No branch matches that.")}</span>
              ) : hits.map((b) => (
                <button key={b} type="button" role="option" aria-selected={value === b} data-testid="wfcat-scope-option" data-id={b}
                  onClick={() => { onChange(b); setOpen(false); }}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 10px", borderRadius: 6, border: "none", background: value === b ? T.p3 : "transparent", color: value === b ? T.p1 : T.n1, fontSize: 12.5, fontWeight: value === b ? 700 : 500, fontFamily: T.font, textAlign: "left", cursor: "pointer" }}>
                  {b}{value === b ? <Icon.check size={12} /> : null}
                </button>
              ))}
            </span>
          </span>
        </>
      ) : null}
    </span>
  );
}

/* Per-LOB branch availability — a searchable multi-select per line of business, all branches
 * selected by default (operator, 2026-08-03). Replaces the chip grid, which did not scale and
 * made "everywhere" look like an exception rather than the norm. */
function LobBranchPicker({ lob, branches, selected, busy, onToggle, onAll, onNone }: {
  lob: { id: string; name: string }; branches: string[]; selected: Set<string>; busy: boolean;
  onToggle: (branch: string, on: boolean) => void; onAll: () => void; onNone: () => void;
}) {
  const t = useT();
  const [open, setOpen] = useState(false); // ui-local
  const [q, setQ] = useState(""); // ui-local
  const hits = branches.filter((b) => b.toLowerCase().includes(q.trim().toLowerCase()));
  const n = selected.size, total = branches.length;
  const label = n === total ? t("workforce.catalogue.allBranches", "All branches") : n === 0 ? t("workforce.catalogue.noBranches", "No branches") : `${n} ${t("workforce.catalogue.ofBranches", "of")} ${total}`;
  return (
    <div data-testid="wfcat-lob-branches" data-id={lob.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "13px 20px", borderBottom: `1px solid ${T.n6}` }}>
      <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        <span style={{ fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{lob.name}</span>
        <span style={{ fontSize: 11, color: n === 0 ? T.orange1 : T.soft }}>
          {n === 0 ? t("workforce.catalogue.notOfferedAnywhere", "Not offered anywhere — its services are unbookable") : n === total ? t("workforce.catalogue.offeredEverywhere", "Offered at every branch") : [...selected].sort().join(" · ")}
        </span>
      </span>
      <span style={{ position: "relative", flexShrink: 0 }}>
        <button type="button" data-testid="wfcat-lob-branch-open" aria-haspopup="listbox" aria-expanded={open} disabled={busy}
          onClick={() => { setOpen((o) => !o); setQ(""); }}
          style={{ display: "inline-flex", alignItems: "center", gap: 8, height: 32, padding: "0 11px", borderRadius: 8, border: `1px solid ${open ? T.p1 : T.border}`, background: T.white, color: T.n1, fontSize: 12.5, fontWeight: 600, fontFamily: T.font, cursor: busy ? "default" : "pointer" }}>
          {busy ? "…" : label}
          <Icon.chev size={12} />
        </button>
        {open ? (
          <>
            <span onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
            <span style={{ position: "absolute", top: 38, right: 0, zIndex: 41, width: 280, padding: 8, borderRadius: 10, border: `1px solid ${T.border}`, background: T.white, boxShadow: T.shadowLg, display: "flex", flexDirection: "column", gap: 6 }}>
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="wfcat-lob-branch-search"
                placeholder={t("workforce.catalogue.searchBranches", "Search branches…")}
                style={{ height: 32, padding: "0 10px", borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 12.5, fontFamily: T.font, color: T.n1 }} />
              <span style={{ display: "flex", gap: 6 }}>
                <button type="button" data-testid="wfcat-lob-branch-all" onClick={onAll}
                  style={{ flex: 1, padding: "5px 8px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.white, color: T.p1, fontSize: 11.5, fontWeight: 700, fontFamily: T.font, cursor: "pointer" }}>
                  {t("workforce.catalogue.selectAll", "Select all")}
                </button>
                <button type="button" data-testid="wfcat-lob-branch-none" onClick={onNone}
                  style={{ flex: 1, padding: "5px 8px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.white, color: T.n2, fontSize: 11.5, fontWeight: 600, fontFamily: T.font, cursor: "pointer" }}>
                  {t("workforce.catalogue.clearAll", "Clear")}
                </button>
              </span>
              <span role="listbox" aria-multiselectable style={{ display: "flex", flexDirection: "column", maxHeight: 240, overflow: "auto" }}>
                {hits.length === 0 ? (
                  <span style={{ padding: 10, fontSize: 12, color: T.soft }}>{t("workforce.catalogue.noBranchMatch", "No branch matches that.")}</span>
                ) : hits.map((b) => {
                  const on = selected.has(b);
                  return (
                    <button key={b} type="button" role="option" aria-selected={on} data-testid="wfcat-lob-branch-option" data-id={`${lob.id}|${b}`}
                      onClick={() => onToggle(b, !on)}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 6, border: "none", background: on ? T.p3 : "transparent", color: on ? T.p1 : T.n1, fontSize: 12.5, fontWeight: on ? 700 : 500, fontFamily: T.font, textAlign: "left", cursor: "pointer" }}>
                      <span style={{ width: 13, height: 13, borderRadius: 3, border: `1.5px solid ${on ? T.p1 : T.border}`, background: on ? T.p1 : T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {on ? <Icon.check size={9} color={T.white} /> : null}
                      </span>
                      {b}
                    </button>
                  );
                })}
              </span>
            </span>
          </>
        ) : null}
      </span>
    </div>
  );
}

/* Service artwork — what a customer sees on the journey (operator, 2026-08-03). A real upload:
 * the file is read to a data URL so it persists through the same override store as every other
 * service field, rather than depending on an asset host that does not exist in this environment.
 * A URL can be pasted instead for artwork already hosted. Preview is drawn at 16:9, the shape the
 * journey uses, so Riley sees the crop he is actually shipping. */
function ImageField({ url, alt, onChange, testid }: {
  url: string; alt: string; onChange: (next: { imageUrl: string; imageAlt: string }) => void; testid: string;
}) {
  const t = useT();
  const [busyRead, setBusyRead] = useState(false); // ui-local
  const [tooBig, setTooBig] = useState(false); // ui-local
  const pick = (file: File | null) => {
    if (!file) return;
    /* Guard the payload: a multi-megabyte data URL would bloat every catalogue response. */
    if (file.size > 900 * 1024) { setTooBig(true); return; }
    setTooBig(false); setBusyRead(true);
    const fr = new FileReader();
    fr.onload = () => { setBusyRead(false); onChange({ imageUrl: String(fr.result || ""), imageAlt: alt || file.name.replace(/\.[^.]+$/, "") }); };
    fr.onerror = () => { setBusyRead(false); };
    fr.readAsDataURL(file);
  };
  const hosted = !url.startsWith("data:");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
      <span style={{ ...cap, fontSize: 10 }}>{t("workforce.catalogue.serviceImage", "Image shown on the journey")}</span>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <label data-testid={testid}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files?.[0] ?? null); }}
          style={{ position: "relative", width: 168, aspectRatio: "16 / 9", borderRadius: 8, overflow: "hidden", border: `1px ${url ? "solid" : "dashed"} ${url ? T.border : T.n4}`, background: url ? T.n6 : alpha(T.n1, 2), cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {url ? (
            <img src={url} alt={alt || ""} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          ) : (
            <span style={{ fontSize: 11.5, color: T.soft, textAlign: "center", padding: "0 10px", lineHeight: 1.45 }}>
              {busyRead ? t("workforce.catalogue.reading", "Reading…") : t("workforce.catalogue.dropOrChoose", "Drop an image, or click to choose")}
            </span>
          )}
          <input type="file" accept="image/*" onChange={(e) => pick(e.target.files?.[0] ?? null)}
            style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer" }} />
        </label>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
          <input value={hosted ? url : ""}
            onChange={(e) => onChange({ imageUrl: e.target.value.trim(), imageAlt: alt })}
            placeholder={hosted ? t("workforce.catalogue.orPasteUrl", "…or paste an image URL") : t("workforce.catalogue.uploadedFile", "Uploaded file")}
            disabled={!hosted} data-testid="wfcat-f-imageurl" style={{ ...INPUT, opacity: hosted ? 1 : 0.6 }} />
          <input value={alt} onChange={(e) => onChange({ imageUrl: url, imageAlt: e.target.value })}
            placeholder={t("workforce.catalogue.altPlaceholder", "Describe the image, for screen readers")}
            data-testid="wfcat-f-imagealt" style={INPUT} />
          {url ? (
            <button type="button" data-testid="wfcat-f-imageclear" onClick={() => onChange({ imageUrl: "", imageAlt: "" })}
              style={{ alignSelf: "flex-start", border: "none", background: "none", padding: 0, cursor: "pointer", color: T.p1, fontSize: 11.5, fontWeight: 700 }}>
              {t("workforce.catalogue.removeImage", "Remove image")}
            </button>
          ) : null}
        </div>
      </div>
      {tooBig ? (
        <span style={{ fontSize: 11, color: T.orange1, fontWeight: 600 }}>{t("workforce.catalogue.imageTooBig", "That file is over 900KB. Use a smaller image, or paste a hosted URL.")}</span>
      ) : url && !alt ? (
        <span style={{ fontSize: 11, color: T.orange1 }}>{t("workforce.catalogue.altMissing", "Add a description — without it the image is invisible to anyone using a screen reader.")}</span>
      ) : (
        <span style={{ fontSize: 11, color: T.soft }}>{t("workforce.catalogue.imageHint", "Shown wherever a customer picks this service — online booking, the lobby kiosk and event listings. Landscape works best.")}</span>
      )}
    </div>
  );
}

function ServiceEditor({ svc, catalogue, busy, onSave, onArchive, onRestore, onDelete, onClose }: {
  svc: CatalogueService | null;            // null = adding
  catalogue: { lobs: { id: string; name: string }[]; intents: { id: string; name: string; lob: string }[]; questionGroups: CatalogueQuestionGroup[] };
  busy: string | null;
  onSave: (draft: ServiceDraft, id: string | null) => void;
  onArchive: ((s: CatalogueService) => void) | null;
  onRestore: ((s: CatalogueService) => void) | null;
  onDelete: ((s: CatalogueService) => void) | null;
  onClose: () => void;
}) {
  const t = useT();
  /* Defend EVERY catalogue array here, at the boundary, rather than trusting each caller: this
     component crashed the whole Services screen once because one new array (questionGroups) was
     missing from the shape a caller passed. Local aliases make that impossible to repeat. */
  const lobOptions = catalogue.lobs ?? [];
  const intentOptions = catalogue.intents ?? [];
  const groupOptions = catalogue.questionGroups ?? [];
  const added = !!svc && svc.id.startsWith("sw_svc_new_");
  const [name, setName] = useState(svc?.name ?? "");
  const [description, setDescription] = useState(svc?.description ?? "");
  const [imageUrl, setImageUrl] = useState(svc?.imageUrl ?? "");
  const [imageAlt, setImageAlt] = useState(svc?.imageAlt ?? "");
  const [lob, setLobId] = useState(svc?.lob ?? lobOptions[0]?.id ?? "");
  const [intent, setIntent] = useState(svc?.intent ?? "");
  const [durationMin, setDuration] = useState(svc?.durationMin ?? 30);
  const [preMin, setPre] = useState(svc?.preMin ?? 0);
  const [postMin, setPost] = useState(svc?.postMin ?? 0);
  const [inPerson, setInPerson] = useState(svc?.channels.inPerson ?? true);
  const [phone, setPhone] = useState(svc?.channels.phone ?? false);
  const [video, setVideo] = useState(svc?.channels.video ?? false);
  const [minAdvance, setMinAdv] = useState(svc?.minAdvance ?? "");
  const [maxAdvance, setMaxAdv] = useState(svc?.maxAdvance ?? "");
  const [minCancel, setMinCancel] = useState(svc?.minCancel ?? "");
  const [bookedBy, setBookedBy] = useState<"customer" | "advisor">(svc?.bookedBy ?? "customer");
  /* Booking modules — WHERE it can be booked (alongside the delivery channels above). */
  const [mAppt, setMAppt] = useState(svc?.modules?.appointments ?? true);
  const [mQueue, setMQueue] = useState(svc?.modules?.queueing ?? false);
  const [mEvent, setMEvent] = useState(svc?.modules?.events ?? false);
  /* ONE booking-question group per method (operator ruling). */
  const [qgAppt, setQgAppt] = useState(svc?.questionGroups?.appointments ?? "");
  const [qgQueue, setQgQueue] = useState(svc?.questionGroups?.queueing ?? "");
  const [qgEvent, setQgEvent] = useState(svc?.questionGroups?.events ?? "");
  const [disableBefore, setDisableBefore] = useState(svc?.disableBefore ?? "");
  const [disableAfter, setDisableAfter] = useState(svc?.disableAfter ?? "");

  const intentsForLob = intentOptions.filter((i) => i.lob === lob);
  /* Changing the line of business invalidates the intent: intents belong to one LOB. */
  const pickLob = (id: string) => { setLobId(id); setIntent((cur) => (intentOptions.some((i) => i.id === cur && i.lob === id) ? cur : "")); };
  const valid = name.trim() && lob && intent;
  const block = { preMin, durationMin, postMin };

  const save = () => onSave({
    name: name.trim(), lob, intent, durationMin, preMin, postMin,
    description: description.trim() || null,
    channels: { inPerson, phone, video },
    modules: { appointments: mAppt, queueing: mQueue, events: mEvent },
    questionGroups: { appointments: mAppt ? (qgAppt || null) : null, queueing: mQueue ? (qgQueue || null) : null, events: mEvent ? (qgEvent || null) : null },
    disableBefore: disableBefore || null, disableAfter: disableAfter || null,
    minAdvance: minAdvance.trim() || null, maxAdvance: maxAdvance.trim() || null, minCancel: minCancel.trim() || null,
    bookedBy,
    imageUrl: imageUrl.trim() || null, imageAlt: imageAlt.trim() || null,
  }, svc?.id ?? null);

  const asDate = (iso: string) => { const [y, m, d] = iso.split("-").map(Number); return new Date(y, (m || 1) - 1, d || 1); };
  const dpPick = (v: string | null, apply: (d: string) => void) => apply(v && v.length >= 10 ? v.slice(0, 10) : "");
  const groupSelect = (value: string, onChange: (v: string) => void, testid: string) => (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} style={INPUT}>
      <option value="">{t("workforce.catalogue.noQuestions", "No questions")}</option>
      {(groupOptions).map((g) => <option key={g.id} value={g.id}>{g.name} ({g.questions})</option>)}
    </select>
  );

  return (
    <Drawer panel="service-edit" width={560} testid="wfcat-editor" onClose={onClose}
      title={svc ? t("workforce.catalogue.editService", "Edit service") : t("workforce.catalogue.addService", "Add a service")}
      sub={svc ? svc.name : t("workforce.catalogue.addSub", "It appears in the catalogue as soon as you save it")}>
      <div style={{ padding: "16px 22px 26px", display: "flex", flexDirection: "column", gap: 18 }}>
        {svc && !added ? (
          <div style={{ display: "flex", gap: 9, alignItems: "flex-start", padding: "11px 13px", borderRadius: 8, background: svc.archived ? alpha(T.orange1, 8) : alpha(T.n1, 3), border: `1px solid ${svc.archived ? alpha(T.orange1, 30) : T.n6}` }}>
            <Icon.info size={13} />
            <span style={{ fontSize: 11.5, color: T.n2, lineHeight: 1.5 }}>
              {svc.archived
                ? t("workforce.catalogue.archivedNote", "This service is archived: it is not bookable and does not appear in any advisor's list. Restore it to put it back in the catalogue.")
                : t("workforce.catalogue.seededNote", "This service came from the seeded catalogue. Your changes are kept as an override — the original definition stays intact underneath.")}
            </span>
          </div>
        ) : null}

        <TextField label={t("workforce.catalogue.serviceName", "Service name")} value={name} onChange={setName}
          placeholder={t("workforce.catalogue.namePlaceholder", "What a customer books")} testid="wfcat-f-name" />

        <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <span style={FIELD_LABEL}>{t("workforce.catalogue.descriptionOptional", "Description (optional)")}</span>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
            data-testid="wfcat-f-description" placeholder={t("workforce.catalogue.descriptionPlaceholder", "What the customer gets, in their words")}
            style={{ ...INPUT, height: "auto", padding: "8px 10px", resize: "vertical", lineHeight: 1.5 }} />
        </label>

        <ImageField url={imageUrl} alt={imageAlt}
          onChange={(next) => { setImageUrl(next.imageUrl); setImageAlt(next.imageAlt); }} testid="wfcat-f-image" />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <span style={FIELD_LABEL}>{t("workforce.catalogue.lineOfBusiness", "Line of business")}</span>
            <select value={lob} onChange={(e) => pickLob(e.target.value)} data-testid="wfcat-f-lob" style={INPUT}>
              {lobOptions.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <span style={FIELD_LABEL}>{t("workforce.catalogue.intent", "Intent")}</span>
            <select value={intent} onChange={(e) => setIntent(e.target.value)} data-testid="wfcat-f-intent" style={INPUT}>
              <option value="">{t("workforce.catalogue.pickAnIntent", "Pick an intent…")}</option>
              {intentsForLob.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
          </label>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <span style={{ ...cap, fontSize: 10 }}>{t("workforce.catalogue.timeItTakes", "Time it takes")}</span>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            <NumField label={t("workforce.catalogue.prep", "Prep")} value={preMin} onChange={setPre} suffix={t("workforce.catalogue.min", "min")} testid="wfcat-f-pre" />
            <NumField label={t("workforce.catalogue.appointment", "Appointment")} value={durationMin} onChange={setDuration} suffix={t("workforce.catalogue.min", "min")} testid="wfcat-f-duration" />
            <NumField label={t("workforce.catalogue.wrapUp", "Wrap-up")} value={postMin} onChange={setPost} suffix={t("workforce.catalogue.min", "min")} testid="wfcat-f-post" />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 13px", borderRadius: 8, background: alpha(T.p1, 4), border: `1px solid ${alpha(T.p1, 18)}` }}>
            <BlockBar s={{ ...(svc ?? ({} as CatalogueService)), ...block, blockMin: preMin + durationMin + postMin } as CatalogueService} />
            <span style={{ fontSize: 12.5, fontWeight: 700, color: T.n1, ...mono }}>{preMin + durationMin + postMin} {t("workforce.catalogue.min", "min")}</span>
            <span style={{ fontSize: 11.5, color: T.soft }}>{t("workforce.catalogue.blockedInTheDay", "blocked in an advisor's day")}</span>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span style={{ ...cap, fontSize: 10 }}>{t("workforce.catalogue.howItCanBeDelivered", "How it can be delivered")}</span>
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
            {([[inPerson, setInPerson, t("workforce.catalogue.branch", "Branch"), "inperson"], [phone, setPhone, t("workforce.catalogue.phone", "Phone"), "phone"], [video, setVideo, t("workforce.catalogue.virtual", "Virtual"), "video"]] as const).map(([on, set, label, key]) => (
              <button key={key} type="button" data-testid="wfcat-f-channel" data-id={key} aria-pressed={on} onClick={() => set(!on)}
                style={{ padding: "6px 13px", borderRadius: 999, border: `1px solid ${on ? T.p1 : T.border}`, background: on ? alpha(T.p1, 10) : T.white, color: on ? T.p1 : T.n2, fontSize: 12.5, fontWeight: on ? 700 : 600, fontFamily: T.font, cursor: "pointer" }}>
                {label}
              </button>
            ))}
          </div>
          {!inPerson && !phone && !video ? (
            <span style={{ fontSize: 11, color: T.orange1, fontWeight: 600 }}>{t("workforce.catalogue.noChannelWarning", "With no channel selected, nobody can book this.")}</span>
          ) : null}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span style={{ ...cap, fontSize: 10 }}>{t("workforce.catalogue.whereItCanBeBooked", "Where it can be booked")}</span>
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
            {([[mAppt, setMAppt, t("workforce.catalogue.modAppointments", "Appointments"), "appointments"], [mQueue, setMQueue, t("workforce.catalogue.modQueueing", "Queueing"), "queueing"], [mEvent, setMEvent, t("workforce.catalogue.modEvents", "Events"), "events"]] as const).map(([on, set, label, key]) => (
              <button key={key} type="button" data-testid="wfcat-f-module" data-id={key} aria-pressed={on} onClick={() => set(!on)}
                style={{ padding: "6px 13px", borderRadius: 999, border: `1px solid ${on ? T.p1 : T.border}`, background: on ? alpha(T.p1, 10) : T.white, color: on ? T.p1 : T.n2, fontSize: 12.5, fontWeight: on ? 700 : 600, fontFamily: T.font, cursor: "pointer" }}>
                {label}
              </button>
            ))}
          </div>
          {!mAppt && !mQueue && !mEvent ? (
            <span style={{ fontSize: 11, color: T.orange1, fontWeight: 600 }}>{t("workforce.catalogue.noModuleWarning", "With no booking method selected, nobody can book this.")}</span>
          ) : null}
          <span style={{ fontSize: 11, color: T.soft }}>{t("workforce.catalogue.modulesHint", "Queueing and Events use the same length and prep/wrap-up as the appointment.")}</span>
        </div>

        {/* ONE booking-question group per method (operator ruling): a kiosk queue asks less than
            a booked appointment, so the group is chosen per method rather than per service. */}
        {mAppt || mQueue || mEvent ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <span style={{ ...cap, fontSize: 10 }}>{t("workforce.catalogue.bookingQuestions", "Booking questions")}</span>
            {mAppt ? (
              <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <span style={FIELD_LABEL}>{t("workforce.catalogue.modAppointments", "Appointments")}</span>
                {groupSelect(qgAppt, setQgAppt, "wfcat-f-qg-appointments")}
              </label>
            ) : null}
            {mQueue ? (
              <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <span style={FIELD_LABEL}>{t("workforce.catalogue.modQueueing", "Queueing")}</span>
                {groupSelect(qgQueue, setQgQueue, "wfcat-f-qg-queueing")}
              </label>
            ) : null}
            {mEvent ? (
              <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <span style={FIELD_LABEL}>{t("workforce.catalogue.modEvents", "Events")}</span>
                {groupSelect(qgEvent, setQgEvent, "wfcat-f-qg-events")}
              </label>
            ) : null}
            <span style={{ fontSize: 11, color: T.soft }}>{t("workforce.catalogue.questionsHint", "Groups are maintained in Booking questions. A service takes one group per method.")}</span>
          </div>
        ) : null}

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <span style={{ ...cap, fontSize: 10 }}>{t("workforce.catalogue.availableBetween", "Available between")}</span>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <span style={FIELD_LABEL}>{t("workforce.catalogue.notBookableBefore", "Not bookable before")}</span>
              <DatePicker size="sm" full showTime={false} allowPast includeDow={false} testid="wfcat-f-before"
                value={disableBefore ? `${disableBefore}T00:00` : ""}
                maxDate={disableAfter ? asDate(disableAfter) : undefined}
                onChange={(v) => dpPick(v, setDisableBefore)} />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <span style={FIELD_LABEL}>{t("workforce.catalogue.notBookableAfter", "Not bookable after")}</span>
              <DatePicker size="sm" full showTime={false} allowPast includeDow={false} testid="wfcat-f-after"
                value={disableAfter ? `${disableAfter}T00:00` : ""}
                minDate={disableBefore ? asDate(disableBefore) : undefined}
                onChange={(v) => dpPick(v, setDisableAfter)} />
            </label>
          </div>
          <span style={{ fontSize: 11, color: T.soft, lineHeight: 1.5 }}>
            {t("workforce.catalogue.windowExplainer", "Outside these dates the service disappears from customer booking, and staff see it greyed out with the date. Leave both empty for always available.")}
          </span>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <span style={{ ...cap, fontSize: 10 }}>{t("workforce.catalogue.bookingWindow", "Booking window")}</span>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            <TextField label={t("workforce.catalogue.minAdvance", "Earliest booking")} value={minAdvance} onChange={setMinAdv} placeholder={t("workforce.catalogue.egHours", "e.g. 2 hours")} testid="wfcat-f-minadv" />
            <TextField label={t("workforce.catalogue.maxAdvance", "Latest booking")} value={maxAdvance} onChange={setMaxAdv} placeholder={t("workforce.catalogue.egWeeks", "e.g. 8 weeks")} testid="wfcat-f-maxadv" />
            <TextField label={t("workforce.catalogue.minCancel", "Cancellation window")} value={minCancel} onChange={setMinCancel} placeholder={t("workforce.catalogue.egHours2", "e.g. 4 hours")} testid="wfcat-f-mincancel" />
          </div>
          <span style={{ fontSize: 11, color: T.soft }}>{t("workforce.catalogue.windowHint", "Leave a field empty and the catalogue shows it as not set, rather than guessing a default.")}</span>
          {/* WHO may initiate a booking — the one axis the module pills do not cover. "Customers"
              implies advisors too: an advisor can always book what a customer can. */}
          <span style={{ ...cap, fontSize: 10, paddingTop: 2 }}>{t("workforce.catalogue.bookedBy", "Who can book it")}</span>
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
            {([["customer", t("workforce.catalogue.customerOrAdvisor", "Customers and advisors")], ["advisor", t("workforce.catalogue.advisorOnly", "Advisors only")]] as const).map(([id, label]) => {
              const on = bookedBy === id;
              return (
                <button key={id} type="button" data-testid="wfcat-f-bookedby" data-id={id} aria-pressed={on} onClick={() => setBookedBy(id)}
                  style={{ padding: "6px 13px", borderRadius: 999, border: `1px solid ${on ? T.p1 : T.border}`, background: on ? alpha(T.p1, 10) : T.white, color: on ? T.p1 : T.n2, fontSize: 12.5, fontWeight: on ? 700 : 600, fontFamily: T.font, cursor: "pointer" }}>
                  {label}
                </button>
              );
            })}
          </div>
          <span style={{ fontSize: 11, color: T.soft }}>
            {bookedBy === "advisor"
              ? t("workforce.catalogue.advisorOnlyHint", "Customers can't book this themselves — an advisor schedules it, typically after qualifying them.")
              : t("workforce.catalogue.customerHint", "Customers can book it themselves, and advisors can book it for them.")}
          </span>
        </div>

        <div style={{ display: "flex", gap: 9, alignItems: "center", paddingTop: 4, flexWrap: "wrap" }}>
          <Btn disabled={!valid || busy !== null} testid="wfcat-save" onClick={save}>
            {busy === "save" ? t("workforce.catalogue.saving", "Saving…") : svc ? t("workforce.catalogue.saveChanges", "Save changes") : t("workforce.catalogue.addToCatalogue", "Add to catalogue")}
          </Btn>
          <Btn variant="secondary" onClick={onClose}>{t("common.actions.cancel", "Cancel")}</Btn>
          {svc && onArchive ? (
            /* An archived row offers RESTORE, not a second archive. Before this the button read
               "Archived", stayed enabled and re-sent the same archive — a silent no-op. */
            svc.archived && !added ? (
              <Btn variant="secondary" disabled={busy !== null} testid="wfcat-restore" onClick={() => onRestore && onRestore(svc)}>
                {busy === "save" ? t("workforce.catalogue.saving", "Saving…") : t("workforce.catalogue.restore", "Restore to the catalogue")}
              </Btn>
            ) : (
              <Btn variant="secondary" disabled={busy !== null} testid="wfcat-archive" onClick={() => onArchive(svc)}>
                {t("workforce.catalogue.archive", "Archive")}
              </Btn>
            )
          ) : null}
          {/* DELETE is for live environments and never touches the seeded catalogue (operator
              ruling): only a row Riley added can be deleted, and it asks first. */}
          {svc && added && onDelete ? (
            <Btn variant="secondary" disabled={busy !== null} testid="wfcat-delete" onClick={() => onDelete(svc)}>
              {t("workforce.catalogue.delete", "Delete…")}
            </Btn>
          ) : null}
          {!valid ? <span style={{ fontSize: 11.5, color: T.soft }}>{t("workforce.catalogue.needNameLobIntent", "Name, line of business and intent are required.")}</span> : null}
        </div>
      </div>
    </Drawer>
  );
}

export default function WorkforceServiceCatalogue({ mode = "config", branch = null, rail = "appt-services" }: {
  /** "config" = Riley. "view" = branch manager / advisor: same rows, no controls. */
  mode?: "config" | "view";
  branch?: string | null;
  rail?: string;
}) {
  const t = useT();
  const [data, setData] = useState<ServiceCatalogue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState(""); // ui-local
  const [lob, setLob] = useState<string | null>(null); // ui-local
  const [scope, setScope] = useState<string | null>(branch); // ui-local (branch scope)
  const [tab, setTab] = useState<"services" | "branches" | "import">("services"); // ui-local
  const [staged, setStaged] = useState<(ParsedImport & { fileName: string; fileSize: number; fileText: string; uploadedAt: string; check: ImportValidation }) | null>(null); // ui-local
  const [openRun, setOpenRun] = useState<string | null>(null); // ui-local (which run's failures are expanded)
  const [detail, setDetail] = useState<CatalogueService | null>(null); // ui-local
  /* Editing state (Riley only). `editing` holds the row being edited; "new" means the add
     form. Kept separate from `detail` so the read-only audiences can never reach it. */
  const [editing, setEditing] = useState<CatalogueService | "new" | null>(null); // ui-local
  const [confirmDelete, setConfirmDelete] = useState<CatalogueService | null>(null); // ui-local
  const [newLob, setNewLob] = useState(""); // ui-local
  const [newIntentFor, setNewIntentFor] = useState<string | null>(null); // ui-local (LOB id)
  const [newIntent, setNewIntent] = useState(""); // ui-local
  const { busy, run } = useBusy();
  const config = mode === "config";

  useEffect(() => {
    let live = true;
    setLoading(true); setError(null);
    /* Two audiences, two ops — deliberately. The advisor and the branch manager read the
       SELF-scoped op (`/v1/me/workforce/services`), which is read-only by construction and
       already scoped to their branch and line of business; only Riley reads the admin
       catalogue. Adapted into one shape so the rendering below is identical for both. */
    const p = config
      ? getServiceCatalogue(scope)
      : getMyServiceScope().then((m): ServiceCatalogue => ({
          lobs: m.lobs ?? [], intents: m.intents ?? [], categories: m.intents ?? [],
          /* The read-only audiences see everything their BRANCH offers, not only their own line:
             the branch manager is reading the catalogue, and an advisor needs to know what else
             the branch can do so he knows what to refer on (2026-08-03). */
          services: m.branchServices ?? m.services ?? [], advisors: [], roleLobs: {},
          questionGroups: m.questionGroups ?? [],
          branches: [], branchLobs: {}, scopeBranch: m.locationName, intentLabel: "Intent",
        }));
    p.then((r) => { if (live) { setData(r); setLoading(false); } })
      .catch((e: unknown) => { if (live) { setError(e instanceof Error ? e.message : String(e)); setLoading(false); } });
    return () => { live = false; };
  }, [scope, config]);

  const lobName = (id: string) => data?.lobs.find((l) => l.id === id)?.name ?? id;
  const intentName = (id: string) => data?.intents.find((i) => i.id === id)?.name ?? id;
  const groupName = (id: string | null) => (id ? (data?.questionGroups ?? []).find((g) => g.id === id)?.name ?? id : null);

  /* Every array is defended: the legacy ops/services layer once answered this screen with a
     shape that carried no `intents`, and an undefined .filter() took the whole screen down. */
  const safe = data ? {
    lobs: data.lobs ?? [], intents: data.intents ?? [], services: data.services ?? [],
    advisors: data.advisors ?? [], branches: data.branches ?? [], branchLobs: data.branchLobs ?? {},
    questionGroups: data.questionGroups ?? [],
    intentLabel: data.intentLabel || "Intent",
  } : null;

  /* LOB → intent → services, filtered. Search covers all three levels so "notary",
     "borrow" and "wealth" all find something. */
  const groups = useMemo(() => {
    if (!safe) return [];
    const needle = q.trim().toLowerCase();
    return safe.lobs
      .filter((l) => !lob || l.id === lob)
      .map((l) => {
        const intents = safe.intents.filter((i) => i.lob === l.id).map((i) => {
          const svcs = safe.services.filter((s) => {
            if (s.intent !== i.id) return false;
            if (!needle) return true;
            return [s.name, i.name, l.name].join(" ").toLowerCase().includes(needle);
          });
          return { intent: i, services: svcs };
        }).filter((g) => g.services.length);
        return { lob: l, intents, count: intents.reduce((a, g) => a + g.services.length, 0) };
      })
      .filter((g) => g.count > 0);
  }, [safe, q, lob]);

  const total = safe?.services.length ?? 0;
  const shown = groups.reduce((a, g) => a + g.count, 0);

  const toggleBranchLob = (b: string, id: string, enabled: boolean) => void run(`bl:${b}:${id}`, async () => {
    try { const r = await setBranchLob(b, id, enabled); setData(r); }
    catch (e) { toast({ tone: "danger", title: t("workforce.catalogue.couldntSave", "Couldn't save that"), subtitle: e instanceof Error ? e.message : String(e) }); }
  });
  const toggleBranchSvc = (b: string, id: string, enabled: boolean) => void run(`bs:${b}:${id}`, async () => {
    try { const r = await setBranchService(b, id, enabled); setData(r); }
    catch (e) { toast({ tone: "danger", title: t("workforce.catalogue.couldntSave", "Couldn't save that"), subtitle: e instanceof Error ? e.message : String(e) }); }
  });

  /* Service CRUD. Every write returns the whole catalogue, so the screen adopts the server's
     state rather than patching its own copy — which is what keeps the row, the drawer and the
     advisor's read in agreement. */
  const saveService = (draft: ServiceDraft, id: string | null) => void run("save", async () => {
    try {
      const r = id ? await updateCatalogueService(id, draft) : await createCatalogueService(draft);
      setData(r);
      setEditing(null);
      toast({ tone: "success", title: id ? t("workforce.catalogue.serviceSaved", "Service updated") : t("workforce.catalogue.serviceAdded", "Service added"), subtitle: draft.name });
    } catch (e) {
      toast({ tone: "danger", title: t("workforce.catalogue.couldntSave", "Couldn't save that"), subtitle: e instanceof Error ? e.message : String(e) });
    }
  });
  const archiveService = (s: CatalogueService) => void run("save", async () => {
    try {
      const r = await removeCatalogueService(s.id);
      setData(r);
      setEditing(null);
      toast({ tone: "success", title: r.removed ? t("workforce.catalogue.serviceRemoved", "Service removed") : t("workforce.catalogue.serviceArchived", "Service archived"), subtitle: s.name });
    } catch (e) {
      toast({ tone: "danger", title: t("workforce.catalogue.couldntSave", "Couldn't save that"), subtitle: e instanceof Error ? e.message : String(e) });
    }
  });
  /* Restore is the same PATCH the editor uses for any other field — archiving is a flag, not a
     destruction, so putting a service back needs no separate op. */
  const restoreService = (s: CatalogueService) => void run("save", async () => {
    try {
      const r = await updateCatalogueService(s.id, { archived: false });
      setData(r);
      setEditing(null);
      toast({ tone: "success", title: t("workforce.catalogue.serviceRestored", "Service restored"), subtitle: s.name });
    } catch (e) {
      toast({ tone: "danger", title: t("workforce.catalogue.couldntSave", "Couldn't save that"), subtitle: e instanceof Error ? e.message : String(e) });
    }
  });

  const deleteService = (s: CatalogueService) => void run("save", async () => {
    try {
      const r = await removeCatalogueService(s.id);
      setData(r);
      setConfirmDelete(null);
      setEditing(null);
      toast({ tone: "success", title: t("workforce.catalogue.serviceDeleted", "Service deleted"), subtitle: s.name });
    } catch (e) {
      toast({ tone: "danger", title: t("workforce.catalogue.couldntDelete", "Couldn't delete that"), subtitle: e instanceof Error ? e.message : String(e) });
    }
  });
  /* Riley extends the TREE, not just its leaves: a new line of business, or a new intent under
     one. Added rows are overlays, so the operator's seed document is never rewritten. */
  const addLob = () => void run("tree", async () => {
    try { const r = await createCatalogueLob({ name: newLob.trim() }); setData(r); setNewLob(""); toast({ tone: "success", title: t("workforce.catalogue.lobAdded", "Line of business added"), subtitle: newLob.trim() }); }
    catch (e) { toast({ tone: "danger", title: t("workforce.catalogue.couldntSave", "Couldn't save that"), subtitle: e instanceof Error ? e.message : String(e) }); }
  });
  const addIntent = (lobId: string) => void run("tree", async () => {
    try { const r = await createCatalogueIntent({ name: newIntent.trim(), lob: lobId }); setData(r); setNewIntent(""); setNewIntentFor(null); toast({ tone: "success", title: t("workforce.catalogue.intentAdded", "Intent added"), subtitle: newIntent.trim() }); }
    catch (e) { toast({ tone: "danger", title: t("workforce.catalogue.couldntSave", "Couldn't save that"), subtitle: e instanceof Error ? e.message : String(e) }); }
  });

  const doImport = () => {
    if (!staged) return;
    void run("import", async () => {
      try {
        const u = getUser();
        const r = await importCatalogueServices({
          rows: staged.check.validRows, by: u.name || "Unknown user",
          /* The PERSONA's own role, not the events-process assignment slot: Riley is an Operations
             admin whose events slot happens to read "System Administrator", and an audit trail
             must never record a higher-privilege role than the actor holds. */
          byRole: u.role || u.assignments?.events || null,
          fileName: staged.fileName, fileSize: staged.fileSize, fileText: staged.fileText, uploadedAt: staged.uploadedAt,
        });
        setData(r);
        setStaged(null);
        toast({
          tone: r.run.failed ? "warn" : "success",
          title: `${r.run.imported} ${t("workforce.catalogue.imported", "services imported")}`,
          subtitle: r.run.failed ? `${r.run.failed} ${t("workforce.catalogue.rowsFailed", "rows failed — see the history below")}` : staged.fileName,
        });
      } catch (e) {
        toast({ tone: "danger", title: t("workforce.catalogue.importFailed", "Import failed"), subtitle: e instanceof Error ? e.message : String(e) });
      }
    });
  };
  const stageFile = (file: File | null) => {
    if (!file) return;
    const fr = new FileReader();
    fr.onload = () => {
      const text = String(fr.result || "");
      const parsed = parseImportCsv(text);
      /* Validate NOW, against the catalogue this screen already holds, so every problem is visible
         while the operator can still fix the spreadsheet. The server re-checks on import. */
      const check = validateImportRows(parsed.rows, {
        lobs: safe?.lobs ?? [], intents: safe?.intents ?? [],
        questionGroups: safe?.questionGroups ?? [],
        existingNames: (safe?.services ?? []).map((x) => x.name),
      });
      setStaged({ ...parsed, check, fileName: file.name, fileSize: file.size, fileText: text, uploadedAt: new Date().toISOString() });
    };
    fr.readAsText(file);
  };

  const chip = (on: boolean, label: string, onClick: () => void, testid: string, id?: string) => (
    <button key={label} type="button" data-testid={testid} data-id={id} aria-pressed={on} onClick={onClick}
      style={{ padding: "5px 11px", borderRadius: 999, border: `1px solid ${on ? T.p1 : T.border}`, background: on ? alpha(T.p1, 10) : T.white, color: on ? T.p1 : T.n2, fontSize: 12, fontWeight: on ? 700 : 600, fontFamily: T.font, cursor: "pointer", whiteSpace: "nowrap" }}>
      {label}
    </button>
  );

  return (
    <Screen rail={rail}>
      <PageHeader
        title={config ? t("workforce.catalogue.title", "Services") : t("workforce.catalogue.titleBranch", "Branch services")}
        subtitle={config
          ? t("workforce.catalogue.subtitle", "The appointment catalogue customers can book — by line of business and intent, with the time each one takes.")
          : t("workforce.catalogue.subtitleBranch", "Everything this branch offers and how long each appointment takes. Set up by Operations.")}
        breadcrumb={false} />
      <DataSourceStrip screen="wfcatalogue" ops={["getServiceCatalogue", "setBranchLob", "setBranchService", "createCatalogueService", "updateCatalogueService", "removeCatalogueService", "createCatalogueLob", "createCatalogueIntent"]} reqId="REQ-128" />

      {config ? (
        <div style={{ display: "flex", gap: 18, padding: "0 24px", borderBottom: `1px solid ${T.n6}`, flexShrink: 0 }}>
          {([["services", t("workforce.catalogue.tabServices", "Catalogue")], ["branches", t("workforce.catalogue.tabBranches", "By branch")], ["import", t("workforce.catalogue.tabImport", "Bulk import")]] as const).map(([id, label]) => (
            <button key={id} type="button" data-testid="wfcat-tab" data-id={id} onClick={() => setTab(id)}
              style={{ padding: "11px 2px", border: "none", background: "none", borderBottom: `2px solid ${tab === id ? T.p1 : "transparent"}`, color: tab === id ? T.p1 : T.n2, fontSize: 13, fontWeight: 700, fontFamily: T.font, cursor: "pointer" }}>
              {label}
            </button>
          ))}
        </div>
      ) : null}

      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 34px", display: "flex", flexDirection: "column", gap: 16 }}>
        {error ? <div style={{ padding: 40, textAlign: "center", color: T.soft }}>{t("workforce.catalogue.couldntLoad", "Couldn't load the catalogue:")} {error}</div>
          : loading || !safe ? <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t("common.status.loading", "Loading…")}</div>
          : tab === "services" ? (
            <>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", flexShrink: 0 }}>
                <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="wfcat-search"
                  placeholder={t("workforce.catalogue.searchPlaceholder", "Search services, intents, lines of business…")}
                  style={{ height: 34, width: 320, padding: "0 12px", borderRadius: 8, border: `1px solid ${T.border}`, fontSize: 13, fontFamily: T.font, color: T.n1 }} />
                <span style={{ fontSize: 11.5, color: T.soft, ...mono }}>{shown === total ? `${total} ${t("workforce.catalogue.services", "services")}` : `${shown} / ${total}`}</span>
                {config ? (
                  <Btn size="sm" testid="wfcat-add" onClick={() => setEditing("new")}>
                    {t("workforce.catalogue.addService", "Add a service")}
                  </Btn>
                ) : null}
                {config && safe.branches.length ? (
                  /* Branch scope is a SEARCHABLE dropdown, not a pill list (operator, 2026-08-03):
                     a bank has far more branches than fit a row of chips. */
                  <span style={{ marginLeft: "auto" }}>
                    <BranchPicker branches={safe.branches} value={scope} onChange={setScope} />
                  </span>
                ) : null}
              </div>
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap", flexShrink: 0 }}>
                {chip(!lob, t("workforce.catalogue.allLobs", "All lines"), () => setLob(null), "wfcat-lob-all")}
                {safe.lobs.map((l) => chip(lob === l.id, l.name, () => setLob(lob === l.id ? null : l.id), "wfcat-lob", l.id))}
              </div>

              {groups.length === 0 ? (
                <div style={{ padding: 32, textAlign: "center", color: T.soft, fontSize: 13 }}>{t("workforce.catalogue.nothingMatches", "Nothing matches that search.")}</div>
              ) : groups.map((g) => (
                <div key={g.lob.id} style={card} data-testid="wfcat-lob-group" data-id={g.lob.id}>
                  <div style={{ padding: "13px 20px", borderBottom: `1px solid ${T.n6}`, display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", background: alpha(T.n1, 2), flexWrap: "wrap" }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{g.lob.name}</span>
                    <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {config ? (
                        newIntentFor === g.lob.id ? (
                          <>
                            <input autoFocus value={newIntent} onChange={(e) => setNewIntent(e.target.value)} data-testid="wfcat-new-intent"
                              placeholder={t("workforce.catalogue.newIntentPlaceholder", "New intent name…")}
                              style={{ height: 28, width: 190, padding: "0 9px", borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 12, fontFamily: T.font, color: T.n1 }} />
                            <Btn size="sm" disabled={!newIntent.trim() || busy !== null} testid="wfcat-add-intent" onClick={() => addIntent(g.lob.id)}>
                              {busy === "tree" ? "…" : t("workforce.catalogue.add", "Add")}
                            </Btn>
                            <Btn size="sm" variant="ghost" onClick={() => { setNewIntentFor(null); setNewIntent(""); }}>{t("common.actions.cancel", "Cancel")}</Btn>
                          </>
                        ) : (
                          <button type="button" data-testid="wfcat-new-intent-open" onClick={() => { setNewIntentFor(g.lob.id); setNewIntent(""); }}
                            style={{ border: "none", background: "none", padding: 0, cursor: "pointer", color: T.p1, fontSize: 11.5, fontWeight: 700 }}>
                            + {t("workforce.catalogue.addIntent", "Add intent")}
                          </button>
                        )
                      ) : null}
                      <span style={{ ...cap, fontSize: 10.5 }}>{g.count} {t("workforce.catalogue.services", "services")}</span>
                    </span>
                  </div>
                  {g.intents.map((ig) => (
                    <div key={ig.intent.id}>
                      <div style={{ padding: "9px 20px", background: T.white, borderBottom: `1px solid ${T.n6}`, display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                        <span style={{ ...cap, fontSize: 10 }}>{safe.intentLabel || "Intent"} · {ig.intent.name}</span>
                      </div>
                      {ig.services.map((s) => (
                        <div key={s.id} data-testid="wfcat-service-row" data-id={s.id} onClick={() => (config ? setEditing(s) : setDetail(s))}
                          style={{ display: "grid", gridTemplateColumns: "minmax(180px,1.6fr) 86px 118px minmax(120px,1fr) 96px", gap: 12, alignItems: "center", padding: "12px 20px", borderBottom: `1px solid ${T.n6}`, cursor: "pointer", opacity: s.archived || s.bookableNow === false ? 0.5 : 1 }}>
                          <span style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                            {s.imageUrl ? (
                              <img src={s.imageUrl} alt="" data-testid="wfcat-row-thumb"
                                style={{ width: 40, height: 24, objectFit: "cover", borderRadius: 4, flexShrink: 0, background: T.n6 }} />
                            ) : null}
                          <span style={{ fontSize: 13, fontWeight: 600, color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {s.name}
                            {s.archived ? <span style={{ fontSize: 10.5, color: T.soft, fontWeight: 600 }}> · {t("workforce.catalogue.archived", "Archived")}</span> : null}
                            {!s.archived && s.unavailableReason === "before-window" ? <span style={{ fontSize: 10.5, color: T.orange1, fontWeight: 700 }}> · {t("workforce.catalogue.fromDate", "from")} {s.disableBefore}</span> : null}
                            {!s.archived && s.unavailableReason === "after-window" ? <span style={{ fontSize: 10.5, color: T.orange1, fontWeight: 700 }}> · {t("workforce.catalogue.endedDate", "ended")} {s.disableAfter}</span> : null}
                            {s.id.startsWith("sw_svc_new_") ? <span style={{ fontSize: 10.5, color: T.p1, fontWeight: 700 }}> · {t("workforce.catalogue.added", "Added")}</span> : null}
                            {s.modules && !s.modules.appointments && (s.modules.queueing || s.modules.events) ? (
                              <span style={{ fontSize: 10.5, color: T.soft, fontWeight: 600 }}> · {s.modules.queueing ? t("workforce.catalogue.modQueueing", "Queueing") : t("workforce.catalogue.modEvents", "Events")}</span>
                            ) : null}
                          </span>
                          </span>
                          <span style={{ fontSize: 12.5, color: T.n1, textAlign: "right", ...mono }}>{s.durationMin}m</span>
                          <span style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
                            <BlockBar s={s} />
                            <span style={{ fontSize: 11.5, color: T.soft, ...mono }}>{s.blockMin}m</span>
                          </span>
                          <span style={{ fontSize: 11.5, color: T.n2 }}>{channelLabel(s.channels, t)}</span>
                          <span style={{ display: "flex", justifyContent: "flex-end" }}>
                            {s.minAdvance
                              ? <span style={{ fontSize: 11, color: T.soft, ...mono }}>{s.minAdvance}</span>
                              : <Pill tone="neutral">{t("workforce.catalogue.notSet", "not set")}</Pill>}
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ))}
              {!config ? (
                <span style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.55 }}>
                  {t("workforce.catalogue.viewOnlyNote", "This catalogue is maintained by Operations. Tap a service to see its length, channels and booking rules.")}
                </span>
              ) : null}
            </>
          ) : tab === "import" ? (
            <>
              {/* 1 — the template. Offered first: the operator's file is usually built days before
                  they open this screen, so the column contract must be gettable up front. */}
              <div style={{ ...card, padding: "18px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
                <span style={cap}>{t("workforce.catalogue.startWithTheTemplate", "Start with the template")}</span>
                <span style={{ fontSize: 12.5, color: T.n2, lineHeight: 1.55 }}>
                  {t("workforce.catalogue.templateBody", "The template carries every column, one filled example row, and notes explaining each field. Lines beginning with # are ignored on import, so the notes can stay while you work.")}
                </span>
                <div style={{ display: "flex", gap: 9, flexWrap: "wrap", alignItems: "center" }}>
                  <Btn size="sm" variant="secondary" testid="wfcat-import-template"
                    onClick={() => downloadText("aperture-services-import-template.csv", importTemplateCsv())}>
                    {t("workforce.catalogue.downloadTemplate", "Download template")}
                  </Btn>
                  <span style={{ fontSize: 11.5, color: T.soft }}>
                    {IMPORT_COLUMNS.length} {t("workforce.catalogue.columns", "columns")} · {IMPORT_COLUMNS.filter((c) => c.required).length} {t("workforce.catalogue.required", "required")}
                  </span>
                </div>
              </div>

              {/* 2 — the upload. Header problems are reported BEFORE anything is sent: a wrong
                  column map is a file problem, not a per-row problem. */}
              <div style={{ ...card, padding: "18px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
                <span style={cap}>{t("workforce.catalogue.uploadYourFile", "Upload your file")}</span>
                <label data-testid="wfcat-import-drop"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); stageFile(e.dataTransfer.files?.[0] ?? null); }}
                  style={{ position: "relative", padding: "22px 18px", borderRadius: 10, border: `1px dashed ${T.n4}`, background: alpha(T.n1, 2), cursor: "pointer", textAlign: "center" }}>
                  <span style={{ fontSize: 12.5, color: T.n2 }}>{t("workforce.catalogue.dropCsv", "Drop a CSV here, or click to choose a file")}</span>
                  <input type="file" accept=".csv,text/csv" onChange={(e) => stageFile(e.target.files?.[0] ?? null)}
                    style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer" }} />
                </label>
                {staged ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 11, padding: "13px 15px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.white }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
                      <span style={{ fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{staged.fileName}</span>
                      <span style={{ fontSize: 11.5, color: T.soft, ...mono }}>{staged.rows.length} {t("workforce.catalogue.rowsFound", "rows found")}</span>
                    </div>

                    {/* File-level problems first: a wrong column map is not a per-row problem. */}
                    {staged.missingRequired.length ? (
                      <span style={{ fontSize: 12, color: T.red1, fontWeight: 600, lineHeight: 1.5 }}>
                        {t("workforce.catalogue.missingColumns", "Missing required columns:")} {staged.missingRequired.join(", ")}
                      </span>
                    ) : null}
                    {staged.unknownHeaders.length ? (
                      <span style={{ fontSize: 11.5, color: T.orange1, lineHeight: 1.5 }}>
                        {t("workforce.catalogue.ignoredColumns", "These columns aren't part of the template and will be ignored:")} {staged.unknownHeaders.join(", ")}
                      </span>
                    ) : null}

                    {/* Row-level check, run before anything is sent. */}
                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <Pill tone={staged.check.validRows.length ? "success" : "danger"} dot>
                        {staged.check.validRows.length} {t("workforce.catalogue.readyToImport", "ready to import")}
                      </Pill>
                      {staged.check.invalidLines.length ? (
                        <Pill tone="warn" dot>{staged.check.invalidLines.length} {t("workforce.catalogue.withProblems", "with problems")}</Pill>
                      ) : (
                        <span style={{ fontSize: 11.5, color: T.green1, fontWeight: 600 }}>{t("workforce.catalogue.allRowsValid", "Every row passed the checks.")}</span>
                      )}
                    </div>

                    {staged.check.issues.length ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "11px 13px", borderRadius: 8, background: alpha(T.orange1, 6), border: `1px solid ${alpha(T.orange1, 26)}`, maxHeight: 190, overflow: "auto" }}>
                        {staged.check.issues.slice(0, 24).map((iss, ii) => (
                          <span key={ii} style={{ fontSize: 11.5, color: T.n2, lineHeight: 1.5 }}>
                            <span style={{ ...mono, color: T.soft }}>{t("workforce.catalogue.lineShort", "Line")} {iss.line}</span>
                            {" — "}<b style={{ fontWeight: 600 }}>{iss.field}</b>{": "}{iss.problem}
                          </span>
                        ))}
                        {staged.check.issues.length > 24 ? (
                          <span style={{ fontSize: 11, color: T.soft }}>+{staged.check.issues.length - 24} {t("workforce.catalogue.moreProblems", "more — download the rows with issues for the full list")}</span>
                        ) : null}
                      </div>
                    ) : null}

                    {/* Two downloads (operator, 2026-08-04): the whole file back, or just the rows
                        that need fixing. The second is a real CSV with the original columns, so it
                        can be corrected and re-imported directly. */}
                    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
                      <button type="button" data-testid="wfcat-staged-download-all" onClick={() => downloadText(staged.fileName, staged.fileText)}
                        style={{ border: "none", background: "none", padding: 0, cursor: "pointer", color: T.p1, fontSize: 11.5, fontWeight: 700 }}>
                        {t("workforce.catalogue.downloadWholeFile", "Download the whole file")}
                      </button>
                      {staged.check.invalidLines.length ? (
                        <button type="button" data-testid="wfcat-staged-download-issues"
                          onClick={() => downloadText(`rows-with-issues-${staged.fileName}`, issueRowsCsv(staged.fileText, staged.check.issues))}
                          style={{ border: "none", background: "none", padding: 0, cursor: "pointer", color: T.p1, fontSize: 11.5, fontWeight: 700 }}>
                          {t("workforce.catalogue.downloadIssueRows", "Download just the rows with issues")}
                        </button>
                      ) : null}
                    </div>

                    <div style={{ display: "flex", gap: 9, flexWrap: "wrap", alignItems: "center" }}>
                      <Btn size="sm" testid="wfcat-import-run"
                        disabled={busy !== null || !staged.check.validRows.length || staged.missingRequired.length > 0}
                        onClick={doImport}>
                        {busy === "import" ? t("workforce.catalogue.importing", "Importing…") : `${t("workforce.catalogue.importRows", "Import")} ${staged.check.validRows.length}`}
                      </Btn>
                      <Btn size="sm" variant="secondary" onClick={() => setStaged(null)}>{t("common.actions.cancel", "Cancel")}</Btn>
                      <span style={{ fontSize: 11, color: T.soft, lineHeight: 1.5 }}>
                        {staged.check.invalidLines.length
                          ? t("workforce.catalogue.skipNote", "Rows with problems are left out — fix them and import again.")
                          : t("workforce.catalogue.perRowNote", "Rows are checked one at a time — good rows are created even if others fail.")}
                      </span>
                    </div>
                  </div>
                ) : null}
              </div>

              {/* 3 — the history, as a COLUMN TABLE (operator, 2026-08-04): Status · Import name ·
                  Upload date/time · Completion date/time, with the download to the right of the
                  completion date. Same content as before, read as rows: two timestamps rather than
                  one, because "handed over" and "finished" answer different questions. Failures
                  expand under their own row, so the table stays scannable when nothing went wrong. */}
              <div style={card}>
                <div style={{ padding: "13px 20px", borderBottom: `1px solid ${T.n6}` }}>
                  <span style={cap}>{t("workforce.catalogue.importHistory", "Import history")}</span>
                </div>
                {!(data?.imports ?? []).length ? (
                  <div style={{ padding: 28, textAlign: "center", fontSize: 13, color: T.soft }}>
                    {t("workforce.catalogue.noImportsYet", "No imports yet. Every run is recorded here with who ran it and what happened to each row.")}
                  </div>
                ) : (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: IMPORT_COLS, gap: 12, padding: "9px 20px", borderBottom: `1px solid ${T.n6}`, background: alpha(T.n1, 2) }}>
                      {[
                        t("workforce.catalogue.colStatus", "Status"),
                        t("workforce.catalogue.colImportName", "Import name"),
                        t("workforce.catalogue.colUploaded", "Upload date/time"),
                        t("workforce.catalogue.colCompleted", "Completion date/time"),
                        "",
                      ].map((h, hi) => <span key={hi} style={{ ...cap, fontSize: 10 }}>{h}</span>)}
                    </div>
                    {(data?.imports ?? []).map((runRow, ri) => {
                      const failed = runRow.failed > 0;
                      const status = runRow.status ?? (failed ? (runRow.imported ? "partial" : "failed") : "completed");
                      const open = openRun === runRow.id;
                      const last = ri === (data?.imports ?? []).length - 1;
                      return (
                        <div key={runRow.id} data-testid="wfcat-import-run" data-id={runRow.id}
                          style={{ borderBottom: last && !open ? "none" : `1px solid ${T.n6}` }}>
                          <div style={{ display: "grid", gridTemplateColumns: IMPORT_COLS, gap: 12, padding: "13px 20px", alignItems: "center" }}>
                            <span>
                              <Pill tone={status === "completed" ? "success" : status === "failed" ? "danger" : "warn"} dot>
                                {status === "completed" ? t("workforce.catalogue.statusCompleted", "Completed")
                                  : status === "failed" ? t("workforce.catalogue.statusFailed", "Failed")
                                  : t("workforce.catalogue.statusPartial", "Completed with errors")}
                              </Pill>
                            </span>
                            <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                              <span style={{ fontSize: 13, fontWeight: 600, color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{runRow.fileName}</span>
                              <span style={{ fontSize: 11, color: T.soft }}>
                                {runRow.imported}/{runRow.rowCount} {t("workforce.catalogue.importedOf", "imported")}
                                {failed ? (
                                  <>
                                    {" · "}
                                    <button type="button" data-testid="wfcat-import-expand" onClick={() => setOpenRun(open ? null : runRow.id)}
                                      style={{ border: "none", background: "none", padding: 0, cursor: "pointer", color: T.red1, fontSize: 11, fontWeight: 700 }}>
                                      {runRow.failed} {open ? t("workforce.catalogue.hideFailed", "failed — hide") : t("workforce.catalogue.showFailed", "failed — show")}
                                    </button>
                                  </>
                                ) : null}
                              </span>
                            </span>
                            <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                              <span style={{ fontSize: 12.5, color: T.n1, ...mono }}>{new Date(runRow.uploadedAt ?? runRow.at).toLocaleString()}</span>
                              <span style={{ fontSize: 11, color: T.soft, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{runRow.by}{runRow.byRole ? ` · ${runRow.byRole}` : ""}</span>
                            </span>
                            <span style={{ fontSize: 12.5, color: T.n1, ...mono }}>{new Date(runRow.completedAt ?? runRow.at).toLocaleString()}</span>
                            {/* Two downloads per run (operator, 2026-08-04): the whole file, or just
                                the rows that failed. The failed-rows file is re-importable. */}
                            <span style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
                              {runRow.fileText ? (
                                <Btn size="sm" variant="secondary" testid="wfcat-import-download"
                                  onClick={() => downloadText(runRow.fileName, runRow.fileText || "")}>
                                  {t("workforce.catalogue.downloadFile", "Whole file")}
                                </Btn>
                              ) : null}
                              {failed ? (
                                <button type="button" data-testid="wfcat-import-download-issues"
                                  onClick={() => downloadText(`rows-with-issues-${runRow.fileName}`,
                                    runRow.fileText
                                      ? issueRowsCsv(runRow.fileText, runRow.results.filter((x) => !x.ok).map((x) => ({ line: x.line, field: x.name, problem: x.reason || "" })))
                                      : failureReportCsv(runRow.fileName, runRow.results))}
                                  style={{ border: "none", background: "none", padding: 0, cursor: "pointer", color: T.p1, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
                                  {t("workforce.catalogue.failedRowsOnly", "Failed rows only")}
                                </button>
                              ) : null}
                            </span>
                          </div>
                          {open && failed ? (
                            <div style={{ padding: "0 20px 14px", display: "flex", flexDirection: "column", gap: 9 }}>
                              <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "11px 13px", borderRadius: 8, background: alpha(T.red1, 5), border: `1px solid ${alpha(T.red1, 22)}` }}>
                                {runRow.results.filter((x) => !x.ok).map((x) => (
                                  <span key={x.line} style={{ fontSize: 11.5, color: T.n2, lineHeight: 1.5 }}>
                                    <span style={{ ...mono, color: T.soft }}>{t("workforce.catalogue.lineShort", "Line")} {x.line}</span>
                                    {" — "}<b style={{ fontWeight: 600 }}>{x.name}</b>{": "}{x.reason}
                                  </span>
                                ))}
                              </div>
                              <button type="button" data-testid="wfcat-import-failreport"
                                onClick={() => downloadText(`failed-rows-${runRow.fileName}`, failureReportCsv(runRow.fileName, runRow.results))}
                                style={{ alignSelf: "flex-start", border: "none", background: "none", padding: 0, cursor: "pointer", color: T.p1, fontSize: 11.5, fontWeight: 700 }}>
                                {t("workforce.catalogue.downloadFailures", "Download the failed rows")}
                              </button>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </>
          ) : (
            <div style={card}>
              <div style={{ padding: "13px 20px", borderBottom: `1px solid ${T.n6}` }}>
                <span style={cap}>{t("workforce.catalogue.whichBranchesRunEachLine", "Which branches run each line")}</span>
              </div>
              {safe.lobs.map((l) => {
                const on = new Set(safe.branches.filter((b) => (safe.branchLobs[b] ?? []).includes(l.id)));
                return (
                  <LobBranchPicker key={l.id} lob={l} branches={safe.branches} selected={on}
                    busy={busy === `bl:${l.id}`}
                    onToggle={(b, next) => toggleBranchLob(b, l.id, next)}
                    onAll={() => safe.branches.filter((b) => !on.has(b)).forEach((b) => toggleBranchLob(b, l.id, true))}
                    onNone={() => [...on].forEach((b) => toggleBranchLob(b, l.id, false))} />
                );
              })}
              <div style={{ padding: "14px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
                <span style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.55 }}>
                  {t("workforce.catalogue.branchNote", "Switching a line off at a branch removes its services from that branch's booking surface. Individual services can be switched off from the branch view of the catalogue.")}
                </span>
                {/* Riley extends the tree here too — a new line of business starts life offered
                    everywhere, the same default as the pickers above. */}
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", paddingTop: 2 }}>
                  <input value={newLob} onChange={(e) => setNewLob(e.target.value)} data-testid="wfcat-new-lob"
                    placeholder={t("workforce.catalogue.newLobPlaceholder", "New line of business…")}
                    style={{ height: 32, width: 236, padding: "0 10px", borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 12.5, fontFamily: T.font, color: T.n1 }} />
                  <Btn size="sm" variant="secondary" disabled={!newLob.trim() || busy !== null} testid="wfcat-add-lob" onClick={addLob}>
                    {busy === "tree" ? t("workforce.catalogue.saving", "Saving…") : t("workforce.catalogue.addLine", "Add line of business")}
                  </Btn>
                </div>
              </div>
            </div>
          )}
      </div>
      {detail ? <ServiceDetail svc={detail} intentName={intentName(detail.intent)} lobName={lobName(detail.lob)} groupName={groupName} onClose={() => setDetail(null)} /> : null}
      {config && editing && safe ? (
        <ServiceEditor svc={editing === "new" ? null : editing}
          catalogue={{ lobs: safe.lobs, intents: safe.intents, questionGroups: safe.questionGroups }}
          busy={busy} onSave={saveService} onArchive={archiveService} onRestore={restoreService}
          onDelete={(s) => setConfirmDelete(s)} onClose={() => setEditing(null)} />
      ) : null}
      {/* Delete asks first, and says what it cannot touch (operator ruling: seeded rows are never
          removed — the engine refuses them 409). */}
      {confirmDelete ? (
        <Modal testid="wfcat-delete-modal" tone="danger" onClose={() => setConfirmDelete(null)}
          title={t("workforce.catalogue.deleteTitle", "Delete this service?")}
          sub={confirmDelete.name}
          footer={
            <>
              <Btn variant="secondary" onClick={() => setConfirmDelete(null)}>{t("common.actions.cancel", "Cancel")}</Btn>
              <Btn variant="danger" disabled={busy !== null} testid="wfcat-delete-confirm" onClick={() => deleteService(confirmDelete)}>
                {busy === "save" ? t("workforce.catalogue.deleting", "Deleting…") : t("workforce.catalogue.deleteConfirm", "Delete service")}
              </Btn>
            </>
          }>
          <p style={{ fontSize: 13, color: T.n2, lineHeight: 1.55, margin: 0 }}>
            {t("workforce.catalogue.deleteBody", "It disappears from the catalogue and from every booking surface. Appointments already booked against it keep their record, but nobody can book it again.")}
          </p>
          <p style={{ fontSize: 12, color: T.soft, lineHeight: 1.55, margin: "9px 0 0" }}>
            {t("workforce.catalogue.deleteUndo", "This cannot be undone. To retire a service temporarily, archive it instead.")}
          </p>
        </Modal>
      ) : null}
    </Screen>
  );
}
